import DocumentPage from './DocumentPage';

export const fleetDocument = {
  slug: 'fleet',
  route: '/fleet',
  title: 'Fleet',
  category: 'People & Operations',
  format: 'DOCX',
  description:
    'The Fleet module in EduX ERP manages vehicles, contracts, services, odometer readings, and fleet configuration masters for transport operations and vehicle cost control.',
  metrics: [
    { label: 'Primary focus', value: 'Vehicle management' },
    { label: 'Core records', value: 'Vehicles, contracts, services' },
    { label: 'Tracking', value: 'Usage and maintenance' },
  ],
  workflow: [
    'Create and maintain vehicle records with model, driver, registration, and operational details.',
    'Track contracts, services, and odometer readings over time to monitor validity, maintenance, and usage.',
    'Configure manufacturers, models, categories, service types, status, tags, and activity types for standardized fleet operations.',
  ],
  sections: [
    {
      id: 'introduction',
      title: 'Introduction',
      body: [
        'The Fleet Module in EduX ERP is used to manage all vehicles efficiently. It helps organizations track vehicle details, manage contracts, record services, monitor fuel consumption through odometer readings, and analyze vehicle costs.',
        'Using this module, companies can maintain complete information about vehicles such as manufacturer, model, contracts, services, and maintenance history. It also provides reporting features to track expenses related to each vehicle.',
      ],
    },
    {
      id: 'fleet-vehicles',
      title: 'Fleet',
      body: [
        'Go to Fleet -> Fleet. The Fleet menu is used to create and manage all vehicles in the company. Here, users can store complete information about each vehicle.',
        'This vehicle can be used for transporting employees for official meetings or company travel.',
      ],
      figure: {
        src: '/assets/doc-images/fleet/image2.png',
        alt: 'Fleet vehicle record with model and driver details',
        caption: 'Vehicle form showing model, license plate, driver, registration, last odometer, and operational tabs.',
      },
      bullets: [
        'Maintain complete information about each vehicle.',
        'Track model, driver, registration, and location details.',
        'Keep vehicle records ready for transport, maintenance, and contract tracking.',
      ],
      subsections: [
        {
          id: 'create-model',
          title: 'How to create a model?',
          body: [
            'Vehicle models are configured before or during vehicle setup so every vehicle record can point to a standardized model definition.',
          ],
          gallery: [
            {
              src: '/assets/doc-images/fleet/image.png',
              alt: 'Create Model form in Fleet',
              caption: 'Create Model form used to define the manufacturer, vehicle type, and model characteristics.',
            },
            {
              src: '/assets/doc-images/fleet/image7.png',
              alt: 'Saved vehicle model record',
              caption: 'Saved vehicle model record showing model information and linked manufacturer.',
            },
          ],
          bullets: [
            'Set the model name.',
            'Choose the manufacturer.',
            'Select the vehicle type and category.',
            'Maintain details such as seats, doors, color, model year, and engine-related values.',
          ],
        },
      ],
    },
    {
      id: 'contracts',
      title: 'Contracts',
      body: [
        'The Contracts menu in the Fleet module is used to manage agreements related to vehicles such as insurance, leasing, maintenance contracts, or service agreements.',
        'These contracts help organizations track vehicle-related expenses and ensure that important agreements are renewed before expiration.',
        'Each contract record contains details such as the vendor providing the service, contract duration, responsible person, vehicle assigned, and cost information.',
        'This feature helps organizations maintain proper documentation of vehicle-related contracts and monitor recurring expenses.',
      ],
      figure: {
        src: '/assets/doc-images/fleet/image3.png',
        alt: 'Fleet vehicle contract record',
        caption: 'Contract record showing reference, vendor, vehicle, responsible user, dates, and cost details.',
      },
      subsections: [
        {
          id: 'contract-states',
          title: 'Contract States',
          body: [
            'In the Fleet module, every contract goes through different states that represent the current stage of the contract lifecycle.',
          ],
          bullets: [
            'Incoming: the contract is created, but its start date has not arrived yet.',
            'In Progress: the contract is currently active and valid.',
            'Expired: the contract end date has passed and it is no longer valid.',
            'Closed: the contract has been manually closed or completed before its expiration date.',
          ],
        },
      ],
    },
    {
      id: 'services',
      title: 'Services',
      body: [
        'The Services submenu in the Fleet module is used to record all maintenance and repair services performed on vehicles.',
        'It helps organizations keep track of vehicle maintenance history, service costs, and service providers.',
        'Whenever a vehicle undergoes maintenance such as oil change, engine repair, tire replacement, or general servicing, the details can be recorded in this menu.',
        'This helps in monitoring vehicle performance and managing maintenance expenses.',
      ],
      figure: {
        src: '/assets/doc-images/fleet/image4.png',
        alt: 'Fleet service record',
        caption: 'Service record showing description, service type, date, cost, vendor, and odometer value.',
      },
      subsections: [
        {
          id: 'service-states',
          title: 'Service States',
          body: [
            'Every service record goes through states that represent the progress of vehicle service work.',
          ],
          bullets: [
            'New: the service record has been created, but work has not started yet.',
            'Running: the vehicle service is currently in progress.',
            'Done: the service has been completed successfully.',
            'Cancelled: the service has been cancelled and will not be performed.',
          ],
        },
      ],
    },
    {
      id: 'odometers',
      title: 'Odometers',
      body: [
        'Go to Fleet -> Odometers. Odometers are used to record the distance traveled by a vehicle.',
        'By maintaining odometer records, companies can monitor vehicle usage, plan maintenance schedules, and analyze vehicle performance.',
        'Odometer readings are also useful for calculating fuel consumption and service intervals.',
      ],
      figure: {
        src: '/assets/doc-images/fleet/image5.png',
        alt: 'Fleet odometer list',
        caption: 'Odometer list showing date, vehicle, driver, odometer value, and unit.',
      },
    },
    {
      id: 'configuration',
      title: 'Configuration',
      body: [
        'The Configuration menu allows users to define vehicle-related master data.',
      ],
      subsections: [
        {
          id: 'manufacturers',
          title: 'Manufacturers',
          body: [
            'Go to Configuration -> Models -> Manufacturers. The Configuration menu allows users to define vehicle-related master data.',
          ],
          figure: {
            src: '/assets/doc-images/fleet/image6.png',
            alt: 'Fleet manufacturer record',
            caption: 'Manufacturer record used as a master reference for fleet models.',
          },
        },
        {
          id: 'models',
          title: 'Models',
          body: [
            'Go to Configuration -> Models -> Models. The Models menu is used to define different vehicle models.',
            'Each model is linked to a manufacturer.',
          ],
          figure: {
            src: '/assets/doc-images/fleet/image7.png',
            alt: 'Fleet model record',
            caption: 'Model record linked to the manufacturer and vehicle type.',
          },
        },
        {
          id: 'categories',
          title: 'Categories',
          body: [
            'Go to Configuration -> Models -> Categories. Categories classify vehicles based on their type or usage.',
          ],
          figure: {
            src: '/assets/doc-images/fleet/image8.png',
            alt: 'Fleet category list',
            caption: 'Vehicle Categories list used to classify vehicles by purpose or type.',
          },
        },
        {
          id: 'service-types',
          title: 'Service Types',
          body: [
            'Go to Configuration -> Services -> Types.',
            'Categories in Service Types help split records into contract-related and service-related definitions.',
          ],
          figure: {
            src: '/assets/doc-images/fleet/image9.png',
            alt: 'Fleet service type record',
            caption: 'Service Type definition showing a category such as Service.',
          },
          bullets: [
            'Contract: includes service types related to agreements or contracts associated with vehicles.',
            'Service: includes types related to vehicle maintenance and service operations.',
          ],
        },
        {
          id: 'status',
          title: 'Status',
          body: [
            'The Status menu defines the current condition or availability of vehicles. It helps track whether the vehicle is available for use or not.',
            'Go to Fleet -> Configuration -> Vehicle -> Status.',
          ],
          figure: {
            src: '/assets/doc-images/fleet/imagea.png',
            alt: 'Fleet vehicle status list',
            caption: 'Vehicle Status list showing available, in service, under maintenance, and sold states.',
          },
        },
        {
          id: 'tags',
          title: 'Tags',
          body: [
            'Tags are used to label vehicles for easy filtering and searching.',
            'Tags help categorize vehicles for better organization.',
            'Go to Fleet -> Configuration -> Vehicle -> Tags.',
          ],
          figure: {
            src: '/assets/doc-images/fleet/imageb.png',
            alt: 'Fleet vehicle tags list',
            caption: 'Vehicle Tags list used for easier search and organization.',
          },
        },
        {
          id: 'activity-types',
          title: 'Activity Types',
          body: [
            'Activity types are used to schedule tasks or reminders related to vehicles.',
            'Users can schedule reminders for upcoming vehicle services or contract renewals.',
            'Go to Fleet -> Configuration -> Activity Types.',
          ],
          figure: {
            src: '/assets/doc-images/fleet/imagec.png',
            alt: 'Fleet activity type record',
            caption: 'Activity Type definition used for maintenance reminders and vehicle follow-up.',
          },
        },
      ],
    },
  ],
  related: ['expenses', 'hr', 'inventory'],
};

export default function FleetPage() {
  return <DocumentPage document={fleetDocument} />;
}
